// Страница „Редакция на изписване" (/inventory/writeoff/:writeoffId/edit) —
// зарежда пълния запис, префилва работния лист и записва през useUpdateWriteoff;
// успехът връща към началния екран със `state.openDoc` (toast „Виж документа").
import { useEffect, useMemo, useState } from "react";
import { App, Spin } from "antd";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useProducts, useUpdateWriteoff } from "../../../hooks/useInventory";
import { getWriteoff } from "../../../api/inventory";
import type { CreateWriteoffPayload, Writeoff } from "../../../api/inventory";
import { useUnitDecimals } from "../components/qty";
import { WriteoffWorksheet } from "./components/WriteoffWorksheet";
import { worksheetInitialFromWriteoff } from "./components/writeoffWorksheetShared";

const HOME_PATH = "/inventory/writeoff";

type Props = {
  // Подават се от layout-а; листът чете менюто и ширината от контекста
  onOpenMobileMenu?: () => void;
  isNarrow?: boolean;
  onRegisterLeaveGuard?: (guard: ((proceed: () => void) => void) | null) => void;
};

export const WriteoffEditPage = ({ onRegisterLeaveGuard }: Props) => {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const navigate = useNavigate();
  const { writeoffId } = useParams();
  const updateMutation = useUpdateWriteoff();

  const [loadedWriteoff, setLoadedWriteoff] = useState<Writeoff | null>(null);
  // Количествата се префилват форматирани по знаците на мерната единица →
  // изчакваме и продуктите (decimalsOf), не само записа.
  const productsQuery = useProducts();
  const decimalsOf = useUnitDecimals();
  const initial = useMemo(
    () =>
      loadedWriteoff && (productsQuery.isSuccess || productsQuery.isError)
        ? worksheetInitialFromWriteoff(loadedWriteoff, decimalsOf)
        : null,
    [loadedWriteoff, productsQuery.isSuccess, productsQuery.isError, decimalsOf],
  );

  useEffect(() => {
    const id = Number(writeoffId);
    if (!id) {
      navigate(HOME_PATH);
      return;
    }
    getWriteoff(id)
      .then(setLoadedWriteoff)
      .catch(() => {
        message.error(t("inventoryPage.errLoad"));
        navigate(HOME_PATH);
      });
  }, [writeoffId, message, navigate, t]);

  const handleSave = (payload: CreateWriteoffPayload) => {
    updateMutation.mutate(
      { id: Number(writeoffId), payload },
      {
        onSuccess: (saved: Writeoff) => {
          navigate(HOME_PATH, { state: { openDoc: saved } });
        },
        onError: (e: unknown) => {
          const err = e as { response?: { data?: { detail?: string } } };
          message.error(err.response?.data?.detail || t("inventoryPage.errWriteoff"));
        },
      },
    );
  };

  if (!initial) {
    return (
      <div className="flex h-full items-center justify-center bg-canvas">
        <Spin size="large" />
      </div>
    );
  }

  return (
    <WriteoffWorksheet
      mode="edit"
      title={`${t("inventoryPage.editWriteoffTitle")} №${writeoffId}`}
      initial={initial}
      saving={updateMutation.isPending}
      onSave={handleSave}
      onRegisterLeaveGuard={onRegisterLeaveGuard}
    />
  );
};

export default WriteoffEditPage;
